import {
  type EntryValue,
  type LookupData,
  lookupOptions,
  type TableColumn,
} from '@k-management/shared';
import type { KeyboardEvent } from 'react';
import { DecimalInput } from '../../components/DecimalInput.js';

/**
 * Moves the focus like a spreadsheet (decision of 01/10/2026): Enter to the
 * same column of the next row, Shift+Enter to the previous. The cells carry
 * `data-row` and `data-col`; Tab keeps the order of the browser.
 */
export function moveOnEnter(event: KeyboardEvent<HTMLElement>): void {
  if (event.key !== 'Enter') return;
  const target = event.target as HTMLElement;
  const row = Number(target.dataset.row);
  const col = target.dataset.col;
  if (!col || Number.isNaN(row)) return;
  event.preventDefault();
  const next = document.querySelector<HTMLElement>(
    `[data-col="${CSS.escape(col)}"][data-row="${row + (event.shiftKey ? -1 : 1)}"]`,
  );
  next?.focus();
}

/** Selects the value when a cell gets the focus, unless something was typed already. */
function selectAll(input: HTMLInputElement) {
  // Selected at once and again after the number is shown as typed; the first
  // character typed replaces the value even when the selection got lost.
  let typed = false;
  input.select();
  input.addEventListener(
    'keydown',
    (event) => {
      const key = (event as { key?: string }).key ?? '';
      if (key.length === 1 && input.selectionStart === input.selectionEnd) input.select();
      typed = true;
    },
    { once: true },
  );
  requestAnimationFrame(() => {
    if (!typed) input.select();
  });
}

/**
 * A cell of the grid unlocked: the field of the type of its column. Esc puts
 * back the value it had when the grid was unlocked.
 */
export function GridCellInput({
  column,
  value,
  rowValues,
  lookups,
  row,
  changed,
  error,
  label,
  placeholder,
  onChange,
  onRevert,
}: {
  column: TableColumn;
  value: EntryValue;
  /** The other values of the row: a sub-type depends on its type. */
  rowValues: Record<string, EntryValue>;
  lookups: LookupData;
  row: number;
  changed: boolean;
  error?: string;
  label: string;
  /** What counts while the cell is empty (the amount of the Base). */
  placeholder?: string;
  onChange: (value: EntryValue) => void;
  onRevert: () => void;
}) {
  const common = {
    'data-row': row,
    'data-col': column.key,
    'aria-label': label,
    'aria-invalid': error ? true : undefined,
    title: error,
    ...(placeholder ? { placeholder } : {}),
    className: ['grid-cell', changed ? 'changed' : '', error ? 'invalid' : ''].join(' ').trim(),
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onRevert();
      }
    },
  };

  if (column.lookup) {
    const options = lookupOptions(column, rowValues, lookups);
    const current = value === null || value === undefined ? '' : String(value);
    return (
      <select
        {...common}
        value={current}
        onChange={(event) => onChange(event.target.value || null)}
      >
        <option value="">—</option>
        {options.map((option) => (
          <option key={option.code} value={option.code}>
            {option.description}
          </option>
        ))}
        {current && !options.some((option) => option.code === current) && (
          <option value={current}>{current}</option>
        )}
      </select>
    );
  }
  switch (column.type) {
    case 'boolean':
      return (
        <input
          {...common}
          type="checkbox"
          checked={value === true}
          onChange={(event) => onChange(event.target.checked)}
        />
      );
    case 'select':
      return (
        <select
          {...common}
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(event) => onChange(event.target.value || null)}
        >
          <option value="" />
          {column.options?.map((option) => (
            <option key={option}>{option}</option>
          ))}
        </select>
      );
    case 'number':
    case 'money':
      return (
        <DecimalInput
          {...common}
          className={`${common.className} number`}
          decimals={column.type === 'money' ? [2, 2] : [0, 4]}
          value={value === null || value === undefined ? '' : String(value)}
          onFocus={(event) => selectAll(event.currentTarget)}
          onChange={(text) => onChange(text === '' ? null : text)}
        />
      );
    case 'tags':
      // Several choices do not fit in a cell: they change in the form of the voice.
      return <span className="muted small">{Array.isArray(value) ? value.join(', ') : ''}</span>;
    default:
      return (
        <input
          {...common}
          type={column.type === 'date' ? 'date' : 'text'}
          value={value === null || value === undefined ? '' : String(value)}
          onFocus={(event) => selectAll(event.currentTarget)}
          onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
        />
      );
  }
}
